/**
 * Resolves a free-text search suggestion (from /api/search/suggestions,
 * upstream natural-language strings like "Philippians 4:6-7 meaning" or
 * "Bible verses about overcoming anxiety") to a real, navigable URL.
 *
 * Fixes the "suggestion click just shows more suggestions" bug: previously
 * VerseSearchClient re-ran the search using the clicked suggestion's text as
 * the new query, which for a topical phrase just produces ANOTHER page of
 * suggestions — a dead loop the user can never escape (2026-07-09 mobile
 * bug report). Every suggestion returned by /api/verse-search now carries a
 * resolved `href` so a tap always navigates to a real page:
 *
 *   1. The suggestion text itself parses as a reference -> verse page.
 *   2. A reference is embedded somewhere in the text -> verse page.
 *   3. The text matches a curated topic -> /verses-about/{topic} page.
 *   4. Otherwise -> the search index page, with the suggestion text
 *      pre-filled and auto-executed (a real navigation + forward progress,
 *      never a silent no-op).
 */
import { parseReference, referenceToSlug, BibleReference } from './bibleReference';
import { TOPICS } from './topics';

/**
 * Scans free text for an embedded Bible reference by testing increasingly
 * short word-windows (up to 5 words) starting at every position, e.g.
 * "Philippians 4:6-7 meaning" -> "Philippians 4:6-7". Prefers the first
 * match that includes a verse number over a chapter-only match, since
 * suggestion phrasing is short and specific references are more useful
 * landing targets than a whole chapter.
 */
export function extractReferenceFromText(text: string): BibleReference | null {
  const words = text.trim().split(/\s+/).filter(Boolean);
  let chapterOnlyFallback: BibleReference | null = null;

  for (let start = 0; start < words.length; start++) {
    const maxLen = Math.min(5, words.length - start);
    for (let len = maxLen; len >= 2; len--) {
      const candidate = words.slice(start, start + len).join(' ');
      const parsed = parseReference(candidate);
      if (parsed.ok) {
        if (parsed.reference.startVerse !== undefined) return parsed.reference;
        if (!chapterOnlyFallback) chapterOnlyFallback = parsed.reference;
      }
    }
  }
  return chapterOnlyFallback;
}

// Keyword synonyms per topic slug, beyond the topic's own title/slug words —
// matches the phrasing the upstream suggestions API actually returns (e.g.
// "worry"/"stress" for the anxiety topic, not just the word "anxiety").
const TOPIC_KEYWORDS: Record<string, string[]> = {
  trinity: ['trinity', 'three persons', 'father son and holy spirit'],
  prayer: ['prayer', 'pray', 'praying', 'prayed'],
  wisdom: ['wisdom', 'wise', 'discernment'],
  anxiety: ['anxiety', 'anxious', 'worry', 'worried', 'stress', 'fear', 'afraid', 'peace of mind'],
  grief: ['grief', 'grieving', 'suffering', 'suffer', 'loss', 'mourning', 'mourn'],
  salvation: ['salvation', 'saved', 'being saved', 'eternal life', 'born again'],
  'holy-spirit': ['holy spirit'],
  marriage: ['marriage', 'married', 'husband', 'wife', 'spouse', 'wedding'],
  forgiveness: ['forgiveness', 'forgive', 'forgiving', 'forgiven'],
};

/** Match free text against a curated topic page by keyword. Checked AFTER reference extraction (a specific verse is a better landing target than a topic list). */
export function matchTopicSlug(text: string): string | null {
  const lower = text.toLowerCase();
  for (const topic of TOPICS) {
    const keywords = TOPIC_KEYWORDS[topic.slug] ?? [topic.slug.replace(/-/g, ' ')];
    if (keywords.some((k) => lower.includes(k))) return topic.slug;
  }
  return null;
}

/**
 * Resolve one suggestion string to a navigable href. Never returns null —
 * the search-page fallback guarantees every suggestion tap goes somewhere
 * real, per the "never a dead loop" requirement.
 */
export function resolveSuggestionHref(text: string): string {
  const direct = parseReference(text);
  if (direct.ok) return `/bible-verse-explained/${referenceToSlug(direct.reference)}`;

  const embedded = extractReferenceFromText(text);
  if (embedded) return `/bible-verse-explained/${referenceToSlug(embedded)}`;

  const topicSlug = matchTopicSlug(text);
  if (topicSlug) return `/verses-about/${topicSlug}`;

  return `/bible-verse-explained?q=${encodeURIComponent(text)}`;
}
